// eslint-disable-next-line no-restricted-imports
import { fromJS, Map, OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
import http, { jsonToImmutable, jsonToImmutableError } from '@gonfalon/legacy-http-client/httpUtils';
import type { ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import { normalize, schema } from 'normalizr';

import type { LaunchDarklyIntegrationsManifest } from '#types/generated/integrationSchema';
import { convertMapToOrderedMap } from '#utils/collectionUtils';
import type { Environment } from '#utils/environments/records/Environment';
import type {
  GoaltenderSubscription,
  GoaltenderSubscriptionType,
  IntegrationsManifestServed,
  ManifestRecord,
} from '#utils/goaltenderUtils';
import { createGoaltenderSubscription as createGoaltenderSubscriptionRecord } from '#utils/goaltenderUtils';
import { getProjAndEnvFromSelfLink } from '#utils/integration/getProjAndEnvFromSelfLink';
import type { Destination } from '#utils/integration/records/Destination';
import { createDestination as createDestinationRecord } from '#utils/integration/records/Destination';
import type { SlackIncomingHook } from '#utils/integration/records/SlackIncomingHook';
import { createSlackIncomingHook as createSlackIncomingHookRecord } from '#utils/integration/records/SlackIncomingHook';
import { createJsonPatch } from '#utils/patchUtils';
import type { Project } from '#utils/projects/records/Project';
import { truncateString } from '#utils/stringUtils';
import type { TriggerType } from '#utils/triggerUtils';
import { TriggerPatchKind } from '#utils/triggerUtils';

const destinations = new schema.Entity('destinations', {}, { idAttribute: '_id' });

export type DestinationResponse = ImmutableMap<{
  entities: ImmutableMap<{
    destinations: OrderedMap<string, Destination>;
  }>;
  result: ImmutableMap<{
    items: string[];
    totalCount: number;
  }>;
}>;

function getAllDestinations(
  projects: OrderedMap<string, Project>,
  environments: OrderedMap<string, Environment>,
): Promise<DestinationResponse> {
  return http
    .get('/api/v2/destinations')
    .then((res) =>
      res.json().then((jsonRes) => {
        const data = normalize(jsonRes, { items: [destinations] });
        return fromJS(data).withMutations((map: DestinationResponse) => {
          map.updateIn(['entities', 'destinations'], (dest: ImmutableMap<Destination>) =>
            convertMapToOrderedMap(dest, data.result.items),
          );
          map.updateIn(['entities', 'destinations'], (dest: ImmutableMap<Destination>) =>
            dest
              ? dest.map(createDestinationRecord).map((destinationRecord) => {
                  const { proj, env } = getProjAndEnvFromSelfLink(projects, environments, destinationRecord.selfLink());
                  return destinationRecord
                    .update('environment', () => env as Environment)
                    .update('project', () => proj as Project);
                })
              : OrderedMap(),
          );
        });
      }),
    )
    .catch(jsonToImmutableError);
}

export const createDestination = (destination: Destination): Promise<Destination> => {
  const projKey = destination.project.key;
  const envKey = destination.environment.key;
  return http
    .post(`/api/v2/destinations/${projKey}/${envKey}`, {
      body: destination.toRep(),
    })
    .then(jsonToImmutable)
    .then(createDestinationRecord)
    .then((props) => props.update('environment', () => destination.environment))
    .then((props) => props.update('project', () => destination.project))
    .catch(jsonToImmutableError);
};

type SnowflakeV2SetupRequestBody = {
  name?: string;
  snowflakeHostAddress?: string;
  includeNetworkPolicy?: boolean;
  databaseName?: string;
  warehouseName?: string;
  roleName?: string;
  schemaName?: string;
  userName?: string;
};

type SnowflakeV2SetupResponse = {
  script?: string;
  publicKey?: string;
  publicKeyPkcs8?: string;
  public_key?: string;
  public_key_pkcs8?: string;
};

export const generateSnowflakeV2SetupScript = ({
  projectKey,
  environmentKey,
  body,
}: {
  projectKey: string;
  environmentKey: string;
  body: SnowflakeV2SetupRequestBody;
}): Promise<SnowflakeV2SetupResponse> =>
  http
    .post(`/api/v2/destinations/projects/${projectKey}/environments/${environmentKey}/kinds/snowflake-v2/setup`, {
      body,
    })
    .then((res) => res.json())
    .catch(jsonToImmutableError);

type SnowflakeV2CompleteSetupRequestBody = {
  snowflakeHostAddress: string;
  publicKey: string;
  publicKeyPkcs8: string;
};

type SnowflakeV2CompleteSetupResponse = {
  id?: string;
  _id?: string;
  createdAt?: number;
  maintainerId?: string;
  _links?: {
    self?: {
      href?: string;
    };
  };
};

export const completeSnowflakeV2DestinationSetup = ({
  projectKey,
  environmentKey,
  body,
}: {
  projectKey: string;
  environmentKey: string;
  body: SnowflakeV2CompleteSetupRequestBody;
}): Promise<SnowflakeV2CompleteSetupResponse> =>
  http
    .post(
      `/api/v2/destinations/projects/${projectKey}/environments/${environmentKey}/kinds/snowflake-v2/complete-setup`,
      {
        body,
      },
    )
    .then((res) => res.json())
    .catch(jsonToImmutableError);

type BigQueryCompleteSetupRequestBody = {
  name?: string;
  serviceAccountEmail: string;
  projectId: string;
  schema: string;
  gcsBucketName: string;
  gcsBucketRegion: string;
};

type BigQueryCompleteSetupResponse = {
  id?: string;
  _id?: string;
  createdAt?: number;
  maintainerId?: string;
  _links?: {
    self?: {
      href?: string;
    };
  };
};

export const completeBigQueryDestinationSetup = ({
  projectKey,
  environmentKey,
  body,
}: {
  projectKey: string;
  environmentKey: string;
  body: BigQueryCompleteSetupRequestBody;
}): Promise<BigQueryCompleteSetupResponse> =>
  http
    .post(`/api/v2/destinations/projects/${projectKey}/environments/${environmentKey}/kinds/bigquery/complete-setup`, {
      body,
    })
    .then((res) => res.json())
    .catch(jsonToImmutableError);

type DatabricksCompleteSetupRequestBody = {
  name?: string;
  metastore: string;
  host: string;
  port?: number;
  httpPath: string;
  catalog: string;
  schema: string;
  accessToken: string;
  // Hive metastore only.
  s3BucketName?: string;
  s3BucketRegion?: string;
  s3AccessId?: string;
  bucketSecretKey?: string;
};

type DatabricksCompleteSetupResponse = {
  id?: string;
  _id?: string;
  _links?: {
    self?: {
      href?: string;
    };
  };
};

export const completeDatabricksDestinationSetup = ({
  projectKey,
  environmentKey,
  body,
}: {
  projectKey: string;
  environmentKey: string;
  body: DatabricksCompleteSetupRequestBody;
}): Promise<DatabricksCompleteSetupResponse> =>
  http
    .post(
      `/api/v2/destinations/projects/${projectKey}/environments/${environmentKey}/kinds/databricks/complete-setup`,
      {
        body,
      },
    )
    .then((res) => res.json())
    .catch(jsonToImmutableError);

type ClickHouseSetupResponse = {
  script?: string;
  generatedUsername?: string;
  s3BucketName?: string;
  iamPolicy?: string;
  trustPolicy?: string;
  egressIp?: string;
};

export const generateClickHouseSetupScript = ({
  projectKey,
  environmentKey,
  clickHouseDatabaseName,
  clickHouseUserName,
  clickHouseS3BucketName,
  clickHouseIncludeHostRestriction,
  clickHouseServiceRoleArn,
  clickHousePassword,
}: {
  projectKey: string;
  environmentKey: string;
  clickHouseDatabaseName?: string;
  clickHouseUserName?: string;
  clickHouseS3BucketName?: string;
  clickHouseIncludeHostRestriction?: boolean;
  clickHouseServiceRoleArn?: string;
  clickHousePassword?: string;
}): Promise<ClickHouseSetupResponse> =>
  http
    .post(`/api/v2/destinations/projects/${projectKey}/environments/${environmentKey}/kinds/clickhouse/setup`, {
      body: {
        ...(clickHouseDatabaseName ? { clickHouseDatabaseName } : {}),
        ...(clickHouseUserName ? { clickHouseUserName } : {}),
        ...(clickHouseS3BucketName ? { clickHouseS3BucketName } : {}),
        ...(clickHouseServiceRoleArn ? { clickHouseServiceRoleArn } : {}),
        ...(clickHousePassword ? { clickHousePassword } : {}),
        ...(clickHouseIncludeHostRestriction !== null && clickHouseIncludeHostRestriction !== undefined
          ? { clickHouseIncludeHostRestriction }
          : {}),
      },
    })
    .then((res) => res.json())
    .catch(jsonToImmutableError);

type ClickHouseCompleteSetupRequestBody = {
  name: string;
  host: string;
  port: number;
  // Optional: when blank, the backend defaults to the export user the setup script creates.
  username?: string;
  password: string;
  s3BucketRegion: string;
  s3IamRoleArn: string;
  databaseName?: string;
  s3BucketName?: string;
};

type ClickHouseCompleteSetupResponse = {
  id?: string;
  _id?: string;
  createdAt?: number;
  maintainerId?: string;
  _links?: {
    self?: {
      href?: string;
    };
  };
};

export const completeClickHouseDestinationSetup = ({
  projectKey,
  environmentKey,
  body,
}: {
  projectKey: string;
  environmentKey: string;
  body: ClickHouseCompleteSetupRequestBody;
}): Promise<ClickHouseCompleteSetupResponse> =>
  http
    .post(
      `/api/v2/destinations/projects/${projectKey}/environments/${environmentKey}/kinds/clickhouse/complete-setup`,
      {
        body,
      },
    )
    .then((res) => res.json())
    .catch(jsonToImmutableError);

// Redshift Data Export (revamped). Field names match BE contract;
// hand-typed surfaces here for review, like ClickHouse above.
// See setup_script_handler.go and complete_setup_handler.go.
export type RedshiftSetupRequestBody = {
  name?: string;
  endpoint: string;
  clusterIdentifier: string;
  clusterRegion: string;
  clusterAwsAccountId: string;
  databaseName?: string;
  schemaName?: string;
  userName?: string;
};

export type RedshiftSetupResponse = {
  script?: string;
  redshiftIAMPermissionsPolicy?: string;
  redshiftIAMTrustPolicy?: string;
  s3BucketName?: string;
};

export const generateRedshiftSetupScript = ({
  projectKey,
  environmentKey,
  body,
}: {
  projectKey: string;
  environmentKey: string;
  body: RedshiftSetupRequestBody;
}): Promise<RedshiftSetupResponse> =>
  http
    .post(`/api/v2/destinations/projects/${projectKey}/environments/${environmentKey}/kinds/redshift/setup`, {
      body,
    })
    .then((res) => res.json())
    .catch(jsonToImmutableError);

export type RedshiftCompleteSetupRequestBody = {
  name?: string;
  iamRoleArn: string;
  s3BucketRegion: string;
};

export type RedshiftCompleteSetupResponse = {
  id?: string;
  _id?: string;
  createdAt?: number;
  maintainerId?: string;
  _links?: {
    self?: {
      href?: string;
    };
  };
  // BE returns the full destination rep on 201. Only the fields the container
  // reads are typed here; the rest is intentionally opaque.
  completedArtifacts?: {
    sqlSetupScript?: string;
    s3BucketName?: string;
  };
};

export const completeRedshiftDestinationSetup = ({
  projectKey,
  environmentKey,
  body,
}: {
  projectKey: string;
  environmentKey: string;
  body: RedshiftCompleteSetupRequestBody;
}): Promise<RedshiftCompleteSetupResponse> =>
  http
    .post(`/api/v2/destinations/projects/${projectKey}/environments/${environmentKey}/kinds/redshift/complete-setup`, {
      body,
    })
    .then((res) => res.json())
    .catch(jsonToImmutableError);

// S3 Data Export. Field names match the BE contract; hand-typed here for review,
// like ClickHouse and Redshift above. See setup_script_handler.go (S3Kind case)
// and complete_setup_handler.go (completeS3Setup).
export type S3SetupResponse = {
  s3BucketName?: string;
  iamPolicy?: string;
  trustPolicy?: string;
  egressIp?: string;
};

export const generateS3SetupPolicies = ({
  projectKey,
  environmentKey,
  s3BucketName,
}: {
  projectKey: string;
  environmentKey: string;
  s3BucketName: string;
}): Promise<S3SetupResponse> =>
  http
    .post(`/api/v2/destinations/projects/${projectKey}/environments/${environmentKey}/kinds/s3/setup`, {
      body: { s3BucketName },
    })
    .then((res) => res.json())
    .catch(jsonToImmutableError);

export type S3CompleteSetupRequestBody = {
  name?: string;
  bucketName: string;
  bucketRegion: string;
  folder?: string;
  fileFormat?: string;
  iamRoleArn: string;
};

export type S3CompleteSetupResponse = {
  id?: string;
  _id?: string;
  createdAt?: number;
  maintainerId?: string;
  _links?: {
    self?: {
      href?: string;
    };
  };
};

export const completeS3DestinationSetup = ({
  projectKey,
  environmentKey,
  body,
}: {
  projectKey: string;
  environmentKey: string;
  body: S3CompleteSetupRequestBody;
}): Promise<S3CompleteSetupResponse> =>
  http
    .post(`/api/v2/destinations/projects/${projectKey}/environments/${environmentKey}/kinds/s3/complete-setup`, {
      body,
    })
    .then((res) => res.json())
    .catch(jsonToImmutableError);

export const updateDestination = (oldDestination: Destination, newDestination: Destination): Promise<Destination> => {
  const patch = createJsonPatch(oldDestination.toJS(), newDestination.toJS(), { shouldTestVersion: true });
  return http
    .patch(oldDestination.selfLink(), {
      body: patch,
    })
    .then(jsonToImmutable)
    .then(createDestinationRecord)
    .then((props) => props.update('environment', () => oldDestination.environment))
    .catch(jsonToImmutableError);
};

export const deleteDestination = (destination: Destination): Promise<Response> =>
  http.delete(destination.selfLink()).catch(jsonToImmutableError);

export const sendDestinationTestEvent = (destination: Destination): Promise<Response> =>
  http.post(`${destination.selfLink()}/test-event`).catch(jsonToImmutableError);

const slack = new schema.Entity('slack', {}, { idAttribute: '_id' });

export type SlackHookResponse = ImmutableMap<{
  entities: ImmutableMap<{
    slack: OrderedMap<string, SlackIncomingHook>;
  }>;
  result: ImmutableMap<{
    items: string[];
    totalCount: number;
  }>;
}>;

function getAllSlackIncomingHooks(): Promise<SlackHookResponse> {
  return http
    .get('/api/v2/integrations/slack')
    .then((res) =>
      res.json().then((jsonRes) => {
        const data = normalize(jsonRes, { items: [slack] });
        return fromJS(data).withMutations((map: SlackHookResponse) => {
          map.updateIn(['entities', 'slack'], (entities: ImmutableMap<Destination>) =>
            convertMapToOrderedMap(entities, data.result.items),
          );
          map.updateIn(['entities', 'slack'], (entities: ImmutableMap<Destination>) =>
            entities ? entities.map(createSlackIncomingHookRecord) : OrderedMap(),
          );
        });
      }),
    )
    .catch(jsonToImmutableError);
}

function createSlackIncomingHook(hook: SlackIncomingHook): Promise<SlackIncomingHook> {
  return http
    .post('/api/v2/integrations/slack', {
      body: hook,
    })
    .then(jsonToImmutable)
    .then(createSlackIncomingHookRecord)
    .catch(jsonToImmutableError);
}

interface IntegrationItem {
  selfLink(): string;
}

function updateIntegration<T extends IntegrationItem>(oldIntegration: T, newIntegration: T): Promise<T> {
  const patch = createJsonPatch(oldIntegration, newIntegration);
  return http
    .patch(oldIntegration.selfLink(), {
      body: patch,
    })
    .then(jsonToImmutable, jsonToImmutableError);
}

function updateSlackIncomingHook(oldHook: SlackIncomingHook, newHook: SlackIncomingHook): Promise<SlackIncomingHook> {
  return updateIntegration<SlackIncomingHook>(oldHook, newHook).then(createSlackIncomingHookRecord);
}

function deleteSlackIncomingHook(hook: SlackIncomingHook): Promise<Response> {
  return http.delete(hook.selfLink()).catch(jsonToImmutableError);
}

function getGoaltenderManifests(integrationKey?: string): Promise<OrderedMap<string, ManifestRecord>> {
  const path = integrationKey ? `/api/v2/integration-manifests/${integrationKey}` : '/api/v2/integration-manifests';
  return http
    .get(path)
    .then((res) =>
      res.json().then((jsonRes) => {
        if (integrationKey && jsonRes.key) {
          return fromJS(
            [jsonRes].reduce(
              (o: { [key: string]: IntegrationsManifestServed }, i: IntegrationsManifestServed) =>
                Object.assign(o, { [i.key]: i }),
              {},
            ),
          );
        }
        if (!jsonRes.items) {
          return Map();
        }
        return fromJS(
          jsonRes.items.reduce(
            (o: { [key: string]: IntegrationsManifestServed }, i: IntegrationsManifestServed) =>
              Object.assign(o, { [i.key]: i }),
            {},
          ),
        ).sort((a: ManifestRecord, b: ManifestRecord) => {
          if (a.get('name') === b.get('name')) {
            return 0;
          }
          return a.get('name') < b.get('name') ? -1 : 1;
        });
      }),
    )
    .catch(jsonToImmutableError);
}

function createGoaltenderSubscription(subscription: GoaltenderSubscription): Promise<GoaltenderSubscription> {
  return http
    .post(subscription.parentLink(), {
      body: subscription,
    })
    .then(jsonToImmutable)
    .then((newSubscription) =>
      createGoaltenderSubscriptionRecord(newSubscription.merge({ _manifest: subscription.get('_manifest') })),
    )
    .catch(jsonToImmutableError);
}

function getAllGoaltenderSubscriptions(): Promise<Array<{ key: string; items: GoaltenderSubscriptionType[] }>> {
  return http
    .get('/api/v2/integrations')
    .then((res) => res.json().then((jsonRes) => jsonRes.items))
    .catch(jsonToImmutableError);
}

export type GoaltenderManifestsAndSubscriptionRes = {
  manifests: OrderedMap<string, ManifestRecord>;
  subscriptions: Array<{
    kind: string;
    records: ImmutableMap<GoaltenderSubscription>;
  }>;
};

function getAllGoaltenderManifestsAndSubscriptions(): Promise<GoaltenderManifestsAndSubscriptionRes> {
  return Promise.all([getGoaltenderManifests(), getAllGoaltenderSubscriptions()]).then(([manifests, integrations]) => {
    const subscriptions = integrations.map((integration) => {
      const kind = integration.key;
      const manifest = manifests.get(kind);
      const records = integration.items.map((sub) =>
        createGoaltenderSubscriptionRecord({
          ...sub,
          _manifest: manifest,
          _links: fromJS(sub._links),
          _status: fromJS(sub._status),
        }),
      );
      return {
        kind,
        records: fromJS(records),
      };
    });

    return {
      manifests,
      subscriptions,
    };
  });
}

function getGoaltenderSubscriptions(
  manifest: ManifestRecord = {} as ManifestRecord,
): Promise<GoaltenderSubscription[]> {
  return http
    .get(`${manifest.getIn(['_links', 'subscriptions', 'href'])}`)
    .then((res) =>
      res.json().then((jsonRes) =>
        fromJS(
          jsonRes.items.map((sub: GoaltenderSubscriptionType) =>
            createGoaltenderSubscriptionRecord({
              ...sub,
              _links: fromJS(sub._links),
              _manifest: manifest,
              _status: fromJS(sub._status),
            }),
          ),
        ),
      ),
    )
    .catch(jsonToImmutableError);
}

function updateGoaltenderSubscription(
  oldSubscription: GoaltenderSubscription,
  newSubscription: GoaltenderSubscription,
) {
  return updateIntegration<GoaltenderSubscription>(oldSubscription, newSubscription).then((sub) =>
    createGoaltenderSubscriptionRecord(sub.set('_manifest', oldSubscription.get('_manifest'))),
  );
}

function deleteGoaltenderSubscription(subscription: GoaltenderSubscription): Promise<Response> {
  return http.delete(subscription.selfLink()).catch(jsonToImmutableError);
}

type GoaltenderTestEventRes = {
  success: boolean;
  errorMessage?: string;
  responseBody: string;
  timestamp: number;
  statusCode: number;
};

function sendGoaltenderTestEvent(subscription: GoaltenderSubscription): Promise<GoaltenderTestEventRes> {
  return http
    .post(`${subscription.selfLink()}/validate`, { beta: true })
    .then((res) =>
      res.json().then((jsonRes) => {
        const { statusCode, error, responseBody, timestamp } = jsonRes;
        const result = { success: true, errorMessage: error, responseBody, timestamp, statusCode };

        // Status code 0 means the 3rd party server never responded (usually a time out).
        if (statusCode === 0) {
          result.success = false;
        } else if (statusCode >= 400) {
          result.success = false;
          // We only send the responseBody if `IncludeErrorResponseBody` is true for a given manifest - if it is enabled we can show the error
          result.errorMessage = `Status code: ${statusCode}${
            responseBody && error ? ` - ${truncateString(error, 120)}` : ''
          }`;
        }
        return result;
      }),
    )
    .catch(jsonToImmutableError);
}

function deleteGoaltenderOAuthToken(integrationKey: string): Promise<Response> {
  return http.delete(`/trust/oauth/connect/${integrationKey}`).catch(jsonToImmutableError);
}

export type TriggerCreateArgs = {
  envKey: string;
  flagKey: string;
  projKey: string;
  kind: string;
  manifest: LaunchDarklyIntegrationsManifest;
};

function createTrigger(triggerCreateArgs: TriggerCreateArgs): Promise<TriggerType> {
  const { envKey, flagKey, projKey, kind, manifest } = triggerCreateArgs;
  return http
    .post(`/api/v2/flags/${projKey}/${flagKey}/triggers/${envKey}`, {
      body: {
        instructions: [
          {
            kind,
          },
        ],
        integrationKey: manifest.key,
      },
    })
    .then((res) => res.json())
    .then((t) => {
      const enriched = t;
      enriched._manifest = manifest;
      enriched.isNew = true;
      return enriched;
    })
    .catch(jsonToImmutableError);
}

function deleteTrigger(trigger: TriggerType): Promise<Response> {
  return http.delete(trigger._links.self.href, {
    beta: true,
  });
}

export type FlagInfo = { flagKey: string; envKey: string; projKey: string };

function getTriggers(flagInfo: FlagInfo, triggerManifests: LaunchDarklyIntegrationsManifest[]): Promise<TriggerType[]> {
  return http
    .get(`/api/v2/flags/${flagInfo.projKey}/${flagInfo.flagKey}/triggers/${flagInfo.envKey}`)
    .then((res) => res.json().then((jsonRes) => jsonRes.items))
    .then((triggers) =>
      triggers.map((trigger: TriggerType) => {
        const enriched = trigger;
        enriched._manifest = triggerManifests.find(
          (manifest) => manifest.key === enriched._integrationKey,
        ) as LaunchDarklyIntegrationsManifest;
        enriched.isNew = false;
        return enriched;
      }),
    )
    .catch(jsonToImmutableError);
}

function patchTrigger(
  trigger: TriggerType,
  kind: string,
  triggerManifest: LaunchDarklyIntegrationsManifest,
): Promise<TriggerType> {
  return http
    .patch(trigger._links.self.href, {
      beta: true,
      body: {
        instructions: [
          {
            kind,
          },
        ],
      },
    })
    .then((res) => res.json())
    .then((triggerRes: TriggerType) => {
      const enriched = triggerRes;
      enriched._manifest = triggerManifest;
      enriched.isNew = kind === TriggerPatchKind.RESET_URL;
      return enriched;
    })
    .catch(jsonToImmutableError);
}

export type DynamicEnumOption = { label: string; value: string };

function getDynamicEnumOptions(integrationKey: string, enumKey: string): Promise<DynamicEnumOption[]> {
  return http
    .get(`/api/v2/integration-manifests/${integrationKey}/dynamic-options/${enumKey}`, { beta: true })
    .then((res) => res.json())
    .then((jsonRes) => jsonRes.options)
    .catch(jsonToImmutableError);
}

type MappedMember = {
  id?: string;
  externalId: string;
  email: string;
  error?: string;
};

export type MappedApprovalMemberRes = {
  mapped: MappedMember[];
  unmapped: MappedMember[];
};

function mapApprovalMembers(integrationKey: string): Promise<MappedMember[]> {
  return http
    .post(`/internal/integrations/${integrationKey}/capabilities/approval/map-members`)
    .then((res) => res.json())
    .then((jsonRes: MappedApprovalMemberRes) => jsonRes.mapped)
    .catch(jsonToImmutableError);
}

export {
  getAllSlackIncomingHooks,
  createSlackIncomingHook,
  updateSlackIncomingHook,
  deleteSlackIncomingHook,
  getAllDestinations,
  getGoaltenderManifests,
  createGoaltenderSubscription,
  getAllGoaltenderSubscriptions,
  getAllGoaltenderManifestsAndSubscriptions,
  getGoaltenderSubscriptions,
  updateGoaltenderSubscription,
  deleteGoaltenderSubscription,
  sendGoaltenderTestEvent,
  deleteGoaltenderOAuthToken,
  createTrigger,
  deleteTrigger,
  getTriggers,
  patchTrigger,
  getDynamicEnumOptions,
  mapApprovalMembers,
};
